import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useHits,
  useInstantSearch,
  usePagination,
} from "react-instantsearch-hooks";
import { useDispatch, useSelector } from "react-redux";
import { formatValue2, shuffleArray } from "../../utils/Utils.js";
import ToggleSwitch from "../../components/ToggleSwitch.jsx";
import PaginationNumeric from "../../components/PaginationNumeric.jsx";
import { addToast, removeAllToasts } from "../../store/app/action.js";
import {
  editProducts,
  getPricing,
  getCustomCategories,
  bulkChangeCustomCategoryAssignment,
  getProductCategoryAssignment,
  getMultipleProductCategoryAssignments,
  bulkSetProductInactive,
} from "../../api/inventory.js";
import { getDocumentCounts } from "../../api/productDocuments.js";
import { createSyncNowJob } from "../../api/externalFeeds";
import CanView from "../../components/CanView.jsx";
import Loader from "../../components/Loader.jsx";
import Badge from "../../components/Badges.jsx";
import { FaFilePen, FaPlug } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { PrimaryButton, SecondaryButton } from "../../components/Buttons.jsx";
import ModalBasic from "../../components/ModalBasic.jsx";
import { FaSquareMinus, FaSquareCheck, FaSquare } from "react-icons/fa6";
import { FaPencil, FaPlus } from "react-icons/fa6";
import DropdownEditMenu from "../../components/DropdownEditMenu.jsx";
import config from "@/config";
import { getProductGeorestrictions } from "@/api/productGeorestrictions.js";
import { getPlans as getMarketplaceCategories } from "@/api/marketplace.js";
import { getProductTags } from "@/api/productTags.js";
import { blockedReasonAtStore } from "../taxRules/complianceCheck.js";

function isPublishedPrice(price) {
  if (!price || price.not_found || !(price.quantity > 0)) return false;
  if (price.retailer_disabled || price.disabled || price.retailer_inactive) return false;
  return true;
}

function marketplaceCategories(data) {
  const raw = Array.isArray(data)
    ? data
    : Array.isArray(data?.categories)
      ? data.categories
      : [];
  return raw
    .map((category) => {
      const id = String(category?.id || category?._id || "").trim();
      const name = String(category?.name || category?.label || "").trim();
      if (!id || !name) return null;
      return { id, name, tag: String(category?.tag || "").trim() };
    })
    .filter(Boolean);
}

async function masterProductsBySku(skus) {
  const quoted = skus
    .map((sku) => `\`${String(sku).replace(/`/g, "")}\``)
    .join(",");
  const node = config.ts.nearestNode;
  const response = await fetch(
    `${node.protocol}://${node.host}/multi_search?x-typesense-api-key=${encodeURIComponent(config.ts.key)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        searches: [{
          collection: config.ts.index,
          q: "*",
          query_by: "title",
          filter_by: `sku_id:=[${quoted}]`,
          group_by: "sku_id",
          group_limit: 1,
          per_page: skus.length,
        }],
      }),
    }
  );
  if (!response.ok) throw new Error("Master menu lookup failed");
  const data = await response.json();
  const result = data?.results?.[0] || {};
  const documents = [];
  (result.grouped_hits || []).forEach((group) => {
    (group.hits || []).forEach((hit) => {
      if (hit?.document) documents.push(hit.document);
    });
  });
  (result.hits || []).forEach((hit) => {
    if (hit?.document) documents.push(hit.document);
  });
  const bySku = {};
  documents.forEach((doc) => {
    if (doc?.sku_id) bySku[doc.sku_id] = doc;
  });
  return bySku;
}

const CustomPagination = ({ currentView }) => {
  const {
    pages,
    currentRefinement,
    nbHits,
    nbPages,
    isFirstPage,
    isLastPage,
    refine,
  } = usePagination();
  useEffect(() => {
    refine?.(0);
  }, [currentView, refine]);
  return (
    <PaginationNumeric
      totalRecords={nbHits}
      currentPage={currentRefinement}
      pages={pages}
      onSelect={refine}
      totalPages={nbPages}
      isFirstPage={isFirstPage}
      isLastPage={isLastPage}
    />
  );
};

const ProductsHits = forwardRef(
  (
    {
      view,
      headers,
      setDisableNav,
      disableNav,
      customUpdated,
      customCreated,
      setSelectedSkuCount,
      setDocumentModalOpen,
      setImageModalOpen,
    },
    ref
  ) => {
    //expose function called "bulkEdit" to parent
    useImperativeHandle(ref, () => ({
      bulkEdit: (op) => {
        switch (op) {
          case "clear-selected":
            setSelectedSkus([]);
            break;
          case "assign-categories":
            setNextOp("bulk-assign");
            setAssigningCategory(selectedSkus);
            break;
          case "edit-price":
            setNextOp("bulk-edit-price");
            setBulkEditingPrice(selectedSkus);
            break;
          case "bulk-deactivate":
            setNextOp("bulk-deactivate");
            setBulkEditingPrice(selectedSkus);
            break;
          case "bulk-reactivate":
            setNextOp("bulk-reactivate");
            setBulkEditingPrice(selectedSkus);
            break;
          case "bulk-unpublish": {
            setNextOp("bulk-unpublish");
            const products = localHits.filter((hit) =>
              selectedSkus.includes(hit.sku_id)
            );
            const unpublished = products.filter(
              (hit) => !fetchedPricing[hit.sku_id]?.quantity
            );
            if (unpublished.length > 0) {
              const publishedProducts = localHits
                .filter((hit) => selectedSkus.includes(hit.sku_id))
                .filter((hit) => fetchedPricing[hit.sku_id]?.quantity);
              const unpublishedCount =
                products.length - publishedProducts.length;
              let content = (
                <div className="text-sm p-4">
                  <div>{`You are trying to unpublish ${selectedSkus.length
                    } products, but ${unpublishedCount} of them ${unpublishedCount > 1 ? "have" : "has"
                    } not been published.`}</div>
                  <div className="font-semibold">{`Do you want to proceed with only unpublishing the ${publishedProducts.length
                    } remaining product${publishedProducts.length > 1 ? "s" : ""
                    }?`}</div>
                  <div className="mt-2">
                    <ul>
                      {products.map((hit) => {
                        let className =
                          "text-sm text-slate-800 dark:text-slate-100";
                        if (
                          publishedProducts.filter(
                            (p) => p.sku_id === hit.sku_id
                          ).length > 0
                        ) {
                          // strikethrough
                          className =
                            "line-through text-sm text-slate-500 dark:text-slate-500";
                        }
                        return (
                          <li className={className}>
                            {hit.title}
                            {hit.subtitle ? ` ${hit.subtitle}` : ""}
                            {hit.volume_label ? ` - ${hit.volume_label}` : ""}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              );
              if (unpublishedCount === 0) {
                content = (
                  <div className=" p-4">
                    <div className="flex text-sm">{`None of the selected products are currently published.`}</div>
                  </div>
                );
              }
              const showErrorModal = {
                title: "Some products have not been published",
                message: content,
                continueAction: () => {
                  setSelectedSkus(publishedProducts.map((hit) => hit.sku_id));
                  setBulkEditingPrice(
                    publishedProducts.map((hit) => hit.sku_id)
                  );
                  setErrorModal(null);
                },
                cancelAction: () => {
                  setNextOp(null);
                  setErrorModal(null);
                },
              };
              setErrorModal(showErrorModal);
            } else {
              setBulkEditingPrice(selectedSkus);
            }
            break;
          }
          case "bulk-publish":
            setNextOp("bulk-publish");
            const products = localHits.filter((hit) =>
              selectedSkus.includes(hit.sku_id)
            );
            const published = products.filter(
              (hit) => fetchedPricing[hit.sku_id]?.quantity
            );
            if (published.length > 0) {
              const unpublishedProducts = localHits
                .filter((hit) => selectedSkus.includes(hit.sku_id))
                .filter((hit) => !fetchedPricing[hit.sku_id]?.quantity);
              const publishedCount =
                products.length - unpublishedProducts.length;

              let content = (
                <div className="text-sm p-4">
                  <div>{`You are trying to publish ${selectedSkus.length
                    } products, but ${publishedCount} of them ${publishedCount > 1 ? "have" : "has"
                    } already been published.`}</div>
                  <div className="font-semibold">{`Do you want to proceed with only publishing the ${unpublishedProducts.length
                    } remaining product${unpublishedProducts.length > 1 ? "s" : ""
                    }?`}</div>
                  <div className="mt-2">
                    {/*<div className="font-semibold">To be published:</div>*/}
                    <ul>
                      {products.map((hit) => {
                        let className =
                          "text-sm text-slate-800 dark:text-slate-100";
                        if (
                          published.filter((p) => p.sku_id === hit.sku_id)
                            .length > 0
                        ) {
                          // strikethrough
                          className =
                            "line-through text-sm text-slate-500 dark:text-slate-500";
                        }
                        return (
                          <li className={className}>
                            {hit.title}
                            {hit.subtitle ? ` ${hit.subtitle}` : ""}
                            {hit.volume_label ? ` - ${hit.volume_label}` : ""}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              );

              if (unpublishedProducts.length === 0) {
                content = (
                  <div className=" p-4">
                    <div className="flex text-sm">{`All selected products have already been published`}</div>
                  </div>
                );
              }

              const showErrorModal = {
                title: "Some products have already been published",
                message: content,

                continueAction:
                  unpublishedProducts.length === 0
                    ? null
                    : () => {
                      setBulkEditingPrice(
                        unpublishedProducts.map((hit) => hit.sku_id)
                      );
                      setSelectedSkus(
                        unpublishedProducts.map((hit) => hit.sku_id)
                      );
                      setErrorModal(null);
                    },
                cancelAction: () => {
                  setNextOp(null);
                  setErrorModal(null);
                },
              };
              setErrorModal(showErrorModal);
            } else {
              setBulkEditingPrice(selectedSkus);
            }

            break;
        }
      },
      refreshDocumentCounts: (skuIds) => {
        if (skuIds && skuIds.length > 0) {
          getDocumentCounts(skuIds).then((res) => {
            const documentCountMap = res.document_count || {};

            const _fetchedDocumentCounts = { ...fetchedDocumentCounts };
            skuIds.forEach((sku) => {
              _fetchedDocumentCounts[sku] = { count: documentCountMap[sku] || 0 };
            });
            setFetchedDocumentCounts(_fetchedDocumentCounts);
          });
        }
      },
      refreshImageOverrides: (skuIds) => {
        // No-op: image_override comes from Typesense, will update on next search
      },
      updateImageOverride: (skuId, imageUrl) => {
        // Update the local hit's image_override field immediately
        setLocalHits(prev => prev.map(hit =>
          hit.sku_id === skuId
            ? { ...hit, image_override: imageUrl || null }
            : hit
        ));
      },
    }));
    const [errorModal, setErrorModal] = useState(null);
    const [randColor, setRandColor] = useState({});
    const [nextOp, setNextOp] = useState(null);
    const [fetchedPricing, setFetchedPricing] = useState({});
    const [fetchedCategories, setFetchedCategories] = useState({});
    const [fetchedDocumentCounts, setFetchedDocumentCounts] = useState({});
    const [geoRules, setGeoRules] = useState([]);
    const [geoCategories, setGeoCategories] = useState([]);
    const [geoTags, setGeoTags] = useState([]);
    const [geoReady, setGeoReady] = useState(false);
    const [masterDocs, setMasterDocs] = useState({});
    const masterRequested = useRef(new Set());
    const pricingDocumentStoreRef = useRef();
    const { selectedLocation, mainContainer, mainScrollContainer } = useSelector(
      (state) => state.app
    );
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = React.useState(null);
    const [confirmDanger, setConfirmDanger] = useState(false);
    const { hits, results } = useHits();
    const [localHits, setLocalHits] = useState(hits);
    const dispatch = useDispatch();
    const { status } = useInstantSearch();
    const navigate = useNavigate();
    const [assigningCategory, setAssigningCategory] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [assignedCategories, setAssignedCategories] = useState({});
    const [selectedSkus, setSelectedSkus] = useState([]);
    const [editList, setEditList] = useState({});
    const { selectedMerchant } = useSelector((state) => state.app);
    const [saving, setSaving] = useState(false);
    const [prevSettings, setPrevSettings] = useState({});
    const [editPricing, setEditPricing] = useState(null);
    const [bulkEditingPrice, setBulkEditingPrice] = useState([]);
    const [syncingSkuId, setSyncingSkuId] = useState(null);
    const [skusInView, setSkusInView] = useState([]);
    const [forceUpdateCategories, setForceUpdateCategories] = useState([]);
    const tableHeaderRef = useRef(null);
    useEffect(() => {
      setSelectedSkus([]);
    }, [skusInView]);

    useEffect(() => {
      setSkusInView(localHits.map((hit) => hit.sku_id));
    }, [localHits]);

    useEffect(() => {
      setSelectedSkuCount(selectedSkus.length);
    }, [selectedSkus]);
    useEffect(() => {
      if (!assigningCategory.length || !availableCategories.length || !nextOp) {
        setAssignedCategories({});
        setEditList({});
        return;
      }
      (async () => {
        const _assignedCategories = {};

        for (let sku of assigningCategory) {
          const product = localHits.find((hit) => hit.sku_id === sku);
          let lookup;
          if (nextOp === "bulk-assign") {
            lookup = fetchedCategories[sku] || [];
          } else if (nextOp === "submit") {
            const categories = await getProductCategoryAssignment(sku);
            lookup = categories.categories.map((cat) => cat.name);
          }
          for (let category of lookup) {
            if (!_assignedCategories[category]) {
              const _category = availableCategories.find(
                (cat) => cat.name === category
              );
              _assignedCategories[_category.name] = { ..._category, count: 0 };
            }
            _assignedCategories[category].count++;
          }
        }
        setAssignedCategories(_assignedCategories);
      })();
    }, [assigningCategory, availableCategories, nextOp]);

    useEffect(() => {
      if (!selectedMerchant) {
        return;
      }
      getColors();
    }, [selectedMerchant]);
    const getColors = useCallback(async () => {
      const res = await getCustomCategories();
      setAvailableCategories(res.categories);
      let a = ["purple", "yellow", "green", "blue", "red", "gray", "dark"];
      shuffleArray(a);
      for (let i = 0; i < res.categories.length; i++) {
        setRandColor((prev) => {
          prev[res.categories[i].name] = a[i % a.length];
          return { ...prev };
        });
      }
    }, []);

    useEffect(() => {
      let _hits = hits.filter((hit) =>
        view === "allProducts" ? !hit.sku_id.startsWith("CXP-") : true
      );
      let skus = {};
      _hits.forEach((hit) => {
        if (skus[hit.sku_id]) {
          skus[hit.sku_id].default_category.push(hit.default_category);
        } else {
          skus[hit.sku_id] = {
            ...hit,
            default_category: [hit.default_category],
          };
        }
      });

      // turn object back into array
      setLocalHits(Object.values(skus));
    }, [hits]);

    const _headers = useMemo(() => {
      if (!editing) {
        return headers;
      } else {
        // find index of "price"
        // add "Sales Price" after "Price"
        const idx = headers.findIndex((header) => header === "Price");
        if (idx === -1) {
          return headers;
        }
        const _headers = [...headers];
        _headers.splice(idx + 1, 0, "Sales Price");
        return _headers;
      }
    }, [headers, editing]);

    useEffect(() => {
      if (editing !== null || loading) {
        setDisableNav(true);
      } else {
        setDisableNav(false);
      }
    }, [loading, editing]);

    const toggleSwitchRef = useCallback(
      (node) => {
        if (node !== null && editing?.y !== undefined) {
          // Get the node's position relative to the viewport
          const nodeRect = node.getBoundingClientRect();
          const scrollY =
            mainContainer.scrollTop +
            nodeRect.top -
            editing.y +
            mainContainer.scrollTop;
          mainContainer.scrollTo({
            top: scrollY,
            behavior: "instant",
          });
        }
      },
      [editing?.y, mainContainer]
    );
    const submit = async () => {
      dispatch(removeAllToasts());
      let successToast = {};
      let promise = null;
      const skuId = localHits[editing?.idx].sku_id;
      let newPricing = fetchedPricing[skuId];
      if (!newPricing || newPricing.not_found) {
        dispatch(
          addToast({
            type: "error",
            message: "Nothing has changed",
            timer: 4,
          })
        );
        return;
      }
      newPricing.sell_price = parseFloat(newPricing.sell_price);
      newPricing.cost = parseFloat(newPricing.cost);
      newPricing.discounted_price = parseFloat(newPricing.discounted_price);

      if (newPricing.discounted_price) {
        if (newPricing.discounted_price > newPricing.sell_price) {
          dispatch(
            addToast({
              type: "error",
              message: "Discounted price cannot be greater than selling price",
              timer: 4,
            })
          );
          return;
        }
      }

      if (newPricing.sell_price < 0) {
        dispatch(
          addToast({
            type: "error",
            message: "Price cannot be negative",
            timer: 4,
          })
        );
        return;
      }
      if (newPricing.cost > newPricing.sell_price && !confirmDanger) {
        dispatch(
          addToast({
            type: "warning",
            message: "Selling at a loss, submit again to confirm",
          })
        );
        setConfirmDanger(true);
        return;
      }
      // selling for free?
      if (newPricing.sell_price === 0 && !confirmDanger) {
        dispatch(
          addToast({
            type: "warning",
            message: "Selling for free, submit again to confirm",
          })
        );
        setConfirmDanger(true);
        return;
      }
      // if less than 50% margin
      if (
        (newPricing.sell_price - newPricing.cost) / newPricing.sell_price <
        0.2 &&
        !confirmDanger
      ) {
        dispatch(
          addToast({
            type: "warning",
            message: "Less than 20% margin, submit again to confirm",
          })
        );
        setConfirmDanger(true);
        return;
      }

      successToast = {
        type: "success",
        message: `${localHits[editing?.idx].title} ${localHits[editing?.idx].subtitle
          } been updated on ${selectedLocation.subname || selectedLocation.name}`,
      };
      promise = null;

      const assignedCategoryIDs = Object.keys(assignedCategories).map(
        (cat) => assignedCategories[cat]._id
      ).filter(Boolean);

      promise = editProducts({
        sku_id: localHits[editing?.idx].sku_id,
        price: newPricing.sell_price,
        discounted_price: newPricing.discounted_price,
        cost: newPricing.cost || 0,
        quantity: newPricing.quantity,
        categories: assignedCategoryIDs,
      });

      setLoading(true);
      try {
        await promise;
        setForceUpdateCategories([skuId]);
        successToast.timer = 4;
        dispatch(addToast(successToast));
        setLoading(false);
        setEditing(null);
        setConfirmDanger(false);
      } catch (e) {
        let message = "please try again";
        try {
          const serverMessage = e.response?.data?.error;
          if (serverMessage) {
            message = serverMessage;
          }
        } catch { }

        dispatch(
          addToast({
            type: "error",
            message: "Error updating pricing, " + message,
            timer: 5,
          })
        );
      } finally {
        setLoading(false);
        setAssigningCategory([]);
        setAssignedCategories({});
        setNextOp(null);
        setEditList({});
      }
    };

    useEffect(() => {
      if (localHits) {
        const storeId = selectedLocation?.store_id;
        if (pricingDocumentStoreRef.current === undefined) {
          pricingDocumentStoreRef.current = storeId;
        } else if (pricingDocumentStoreRef.current !== storeId) {
          pricingDocumentStoreRef.current = storeId;
          setFetchedPricing({});
          setFetchedDocumentCounts({});
          return;
        }

        const allSkus = localHits.map((hit) => hit.sku_id);
        const missingSkus = allSkus.filter((sku) => !fetchedPricing[sku]);
        const missingCategorySkus = allSkus.filter(
          (sku) => !fetchedCategories[sku]
        );
        const missingDocumentCountSkus = allSkus.filter(
          (sku) => !fetchedDocumentCounts[sku]
        );

        if (customCreated && !allSkus.includes(customCreated?.id)) {
          setLocalHits([customCreated, ...localHits]);
        }
        if (
          missingCategorySkus.length !== 0 ||
          forceUpdateCategories.length > 0
        ) {
          getMultipleProductCategoryAssignments([
            ...missingCategorySkus,
            ...forceUpdateCategories,
          ]).then((res) => {
            const categoriesMap = res.categories;
            const _fetchedCategories = { ...fetchedCategories };
            missingCategorySkus.forEach((sku) => {
              _fetchedCategories[sku] = categoriesMap[sku] || {
                not_found: true,
              };
            });
            forceUpdateCategories.forEach((sku) => {
              _fetchedCategories[sku] = categoriesMap[sku] || {
                not_found: true,
              };
            });
            setFetchedCategories(_fetchedCategories);
            if (forceUpdateCategories.length > 0) {
              setForceUpdateCategories([]);
            }
          });
        }
        if (missingSkus.length !== 0) {
          getPricing(missingSkus).then((res) => {
            const pricingMap = {};
            (res.pricing || []).forEach((pricing) => {
              pricingMap[pricing.sku_id] = pricing;
            });

            const _fetchedPricing = { ...fetchedPricing };
            missingSkus.forEach((sku) => {
              _fetchedPricing[sku] = pricingMap[sku] || { not_found: true };
            });
            setFetchedPricing(_fetchedPricing);
          });
        }
        if (missingDocumentCountSkus.length !== 0) {
          getDocumentCounts(missingDocumentCountSkus).then((res) => {
            const documentCountMap = res.document_count || {};

            const _fetchedDocumentCounts = { ...fetchedDocumentCounts };
            missingDocumentCountSkus.forEach((sku) => {
              _fetchedDocumentCounts[sku] = { count: documentCountMap[sku] || 0 };
            });
            setFetchedDocumentCounts(_fetchedDocumentCounts);
          });
        }
        getColors();
      }
    }, [
      localHits,
      customCreated,
      forceUpdateCategories,
      selectedLocation?.store_id,
    ]);

    useEffect(() => {
      if (view !== "myProducts") return undefined;
      let cancelled = false;
      Promise.all([
        getProductGeorestrictions(),
        getMarketplaceCategories(),
        getProductTags(),
      ]).then(([ruleData, categoryData, tagData]) => {
        if (cancelled) return;
        setGeoRules(Array.isArray(ruleData) ? ruleData : []);
        setGeoCategories(marketplaceCategories(categoryData));
        setGeoTags(Array.isArray(tagData) ? tagData : []);
      }).catch(() => {
        if (cancelled) return;
        setGeoRules([]);
        setGeoCategories([]);
        setGeoTags([]);
      }).finally(() => {
        if (!cancelled) setGeoReady(true);
      });
      return () => {
        cancelled = true;
      };
    }, [view]);

    useEffect(() => {
      if (view !== "myProducts" || !geoReady) return undefined;
      const missing = localHits
        .filter((hit) => isPublishedPrice(fetchedPricing[hit.sku_id]))
        .map((hit) => hit.sku_id)
        .filter((sku) => sku && !masterRequested.current.has(sku));
      if (!missing.length) return undefined;
      missing.forEach((sku) => masterRequested.current.add(sku));
      let cancelled = false;
      masterProductsBySku(missing).then((bySku) => {
        if (cancelled) return;
        setMasterDocs((prev) => {
          const next = { ...prev };
          missing.forEach((sku) => {
            next[sku] = bySku[sku] || null;
          });
          return next;
        });
      }).catch(() => {
        if (cancelled) return;
        setMasterDocs((prev) => {
          const next = { ...prev };
          missing.forEach((sku) => {
            next[sku] = null;
          });
          return next;
        });
      });
      return () => {
        cancelled = true;
        missing.forEach((sku) => masterRequested.current.delete(sku));
      };
    }, [view, geoReady, localHits, fetchedPricing]);

    const restrictionBySku = useMemo(() => {
      if (view !== "myProducts" || !geoReady || !geoRules.length) return {};
      const reasons = {};
      localHits.forEach((hit) => {
        if (!isPublishedPrice(fetchedPricing[hit.sku_id])) return;
        if (!Object.prototype.hasOwnProperty.call(masterDocs, hit.sku_id)) return;
        const master = masterDocs[hit.sku_id];
        const product = master
          ? {
            ...hit,
            ...master,
            default_category: master.default_category ?? hit.default_category,
            attribute_tags: master.attribute_tags ?? hit.attribute_tags,
          }
          : hit;
        const reason = blockedReasonAtStore({
          hit: product,
          rules: geoRules,
          categories: geoCategories,
          tagOptions: geoTags,
          store: selectedLocation,
        });
        if (reason) reasons[hit.sku_id] = reason;
      });
      return reasons;
    }, [
      view,
      geoReady,
      geoRules,
      geoCategories,
      geoTags,
      localHits,
      fetchedPricing,
      masterDocs,
      selectedLocation,
    ]);

    const moveHeaderOnResize = () => {
      moveHeader(true);
    }
    const moveHeader = (resized = false) => {
      if (!tableHeaderRef?.current) {
        return;
      }
      const headerBoundingClient = tableHeaderRef.current.getBoundingClientRect();
      const mainScrollContainerBoundingClient = mainScrollContainer.getBoundingClientRect();

      if (tableHeaderRef.current.style?.position !== 'fixed' || resized) {
        if (headerBoundingClient.top < mainScrollContainerBoundingClient.top || resized) {
          if (tableHeaderRef.current.parentElement.children[1].children[0]) {
            tableHeaderRef.current.style.position = "fixed";
            tableHeaderRef.current.style.top = "4rem";
            tableHeaderRef.current.style.width = tableHeaderRef.current.parentElement.children[1].children[0].getBoundingClientRect().width + 'px';
            tableHeaderRef.current.style.height = "48px";
            tableHeaderRef.current.style.zIndex = "1000";

            const farLeft = tableHeaderRef.current.parentElement.children[1].children[0].children[0]
            const offset = farLeft.getBoundingClientRect().left
            for (let i = 0; i < tableHeaderRef.current.children[0].children.length; i++) {
              const correspondingItem = tableHeaderRef.current.parentElement.children[1].children[0].children[i]
              tableHeaderRef.current.children[0].children[i].style.position = 'absolute';
              tableHeaderRef.current.children[0].children[i].style.top = 0;
              tableHeaderRef.current.children[0].children[i].style.left = correspondingItem.getBoundingClientRect().left - offset + 'px';
            }
          }
        }
      }
      if (tableHeaderRef.current.style?.position === 'fixed' || resized) {
        const firstRow = tableHeaderRef.current.parentElement.children[1].children[0].children[0]
        if (firstRow.getBoundingClientRect().top > tableHeaderRef.current.getBoundingClientRect().top + tableHeaderRef.current.getBoundingClientRect().height) {
          tableHeaderRef.current.removeAttribute("style");
          for (let i = 0; i < tableHeaderRef.current.children[0].children.length; i++) {
            tableHeaderRef.current.children[0].children[i].removeAttribute("style");
          }
        }
      }
    }

    useEffect(() => {

      mainScrollContainer.addEventListener("scroll", moveHeader);
      window.addEventListener("resize", moveHeaderOnResize);
      return () => {
        mainScrollContainer?.removeEventListener("scroll", moveHeader);
        window.removeEventListener("resize", moveHeaderOnResize);
      }
    }, [mainScrollContainer?.current]);

    const modalTitle = useMemo(() => {
      if (nextOp === "bulk-assign") {
        return `Assign categories to ${assigningCategory.length} product${assigningCategory.length > 1 ? "s" : ""
          }`;
      } else if (nextOp === "submit") {
        return `Publish ${assigningCategory.length} product${assigningCategory.length > 1 ? "s" : ""
          } to these categories`;
      }
    }, [nextOp]);
    return (
      <>
        <ModalBasic
          modalOpen={errorModal}
          setModalOpen={() => setErrorModal(null)}
          width="3xl"
          title={errorModal?.title}
        >
          <div>
            <div className="text-slate-800 dark:text-slate-100">
              {errorModal?.message}
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <SecondaryButton onClick={errorModal?.cancelAction}>
                Cancel
              </SecondaryButton>
              {errorModal?.continueAction && (
                <PrimaryButton onClick={errorModal?.continueAction}>
                  Continue
                </PrimaryButton>
              )}
            </div>
          </div>
        </ModalBasic>

        <ModalBasic
          modalOpen={assigningCategory.length}
          setModalOpen={() => {
            setAssigningCategory([]);
            setAssignedCategories({});
            setEditList({});
            setNextOp(null);
          }}
          width="lg"
          title={modalTitle}
        >
          <div>
            {availableCategories.map((category) => {
              let Icon = <FaSquare />;
              let currentState = "not-assigned";
              if (assignedCategories[category.name]) {
                if (
                  assignedCategories[category.name].count ===
                  assigningCategory.length
                ) {
                  currentState = "assigned-all";
                  Icon = <FaSquareCheck />;
                } else if (assignedCategories[category.name].count > 0) {
                  currentState = "assigned-some";
                  Icon = <FaSquareMinus />;
                }
              }
              return (
                <div key={category.name} className="flex items-center gap-2">
                  <SecondaryButton
                    naked
                    onClick={() => {
                      let _editList = { ...editList };
                      if (assignedCategories[category.name]) {
                        _editList[category._id] = "bulk-remove";
                        setAssignedCategories((prev) => {
                          delete prev[category.name];
                          return { ...prev };
                        });
                      } else {
                        _editList[category._id] = "bulk-add";
                        setAssignedCategories((prev) => {
                          prev[category.name] = {
                            ...category,
                            count: assigningCategory.length,
                          };
                          return { ...prev };
                        });
                      }
                      setEditList(_editList);
                    }}
                  >
                    <div className="text-slate-700 text-lg">{Icon}</div>
                  </SecondaryButton>
                  <span>{category.name}</span>
                  {currentState === "assigned-some" && (
                    <span className="text-slate-500">
                      ({assignedCategories[category.name].count} of{" "}
                      {assigningCategory.length})
                    </span>
                  )}
                </div>
              );
            })}
            <div className={"pt-4 flex justify-end gap-2"}>
              <SecondaryButton
                onClick={() => {
                  setAssigningCategory([]);
                  setAssignedCategories({});
                  setEditList({});
                  setNextOp(null);
                }}
                loading={saving}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton
                onClick={async () => {
                  if (nextOp === "submit") {
                    submit();
                    return;
                  }

                  const editedFetchedCategories = JSON.parse(
                    JSON.stringify({ ...fetchedCategories })
                  );

                  for (let skuID of assigningCategory) {
                    for (let category of Object.keys(editList)) {
                      const thisCategoryName = availableCategories.find(
                        (cat) => cat._id === category
                      ).name;

                      if (editList[category] === "bulk-remove") {
                        // Removing category
                        editedFetchedCategories[skuID] =
                          editedFetchedCategories[skuID].filter(
                            (cat) => cat !== thisCategoryName
                          );
                      } else if (editList[category] === "bulk-add") {
                        // Adding category
                        if (
                          !editedFetchedCategories[skuID].includes(
                            thisCategoryName
                          )
                        ) {
                          editedFetchedCategories[skuID].push(thisCategoryName);
                        }
                      }
                      // Ensure categories are unique
                      editedFetchedCategories[skuID] = [
                        ...new Set(editedFetchedCategories[skuID]),
                      ];
                    }
                  }

                  const hasZero = Object.values(editedFetchedCategories).some(
                    (cats) => cats.length === 0
                  );

                  // verify that no products are assigned to 0 categories
                  if (hasZero) {
                    dispatch(
                      addToast({
                        type: "error",
                        message:
                          "Some products are not assigned to any categories",
                        timer: 5,
                      })
                    );
                    return;
                  }

                  setSaving(true);
                  await bulkChangeCustomCategoryAssignment(
                    assigningCategory,
                    editList
                  );
                  setSaving(false);
                  setFetchedCategories(editedFetchedCategories);

                  setAssignedCategories({});
                  setEditList({});
                  setAssigningCategory([]);
                  setNextOp(null);
                }}
                loading={saving}
              >
                Save
              </PrimaryButton>
            </div>
          </div>
        </ModalBasic>
        <table className="table-auto w-full dark:text-slate-300">
          {status === "loading" || status === "stalled" ? (
            <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
              <tr>
                <td>
                  <div className="w-full text-center py-8">
                    <Loader />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <>
              <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700" ref={tableHeaderRef}>
                <tr>
                  {_headers.map((header, idx) => (
                    <th
                      key={"header_" + idx}
                      className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap"
                    >
                      <div className="font-semibold text-left gap-2 flex">
                        {idx === 0 && (
                          <CanView permissions="manager">
                            {/*  checkbox that selects all skus*/}
                            <input
                              disabled={disableNav}
                              type="checkbox"
                              className="form-checkbox"
                              checked={
                                selectedSkus.length ===
                                localHits.filter(
                                  (h) =>
                                    !fetchedPricing[h.sku_id]
                                      ?.retailer_disabled &&
                                    !fetchedPricing[h.sku_id]?.disabled
                                ).length
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedSkus(
                                    localHits
                                      .filter(
                                        (h) =>
                                          !fetchedPricing[h.sku_id]
                                            ?.retailer_disabled &&
                                          !fetchedPricing[h.sku_id]?.disabled
                                      )
                                      .map((hit) => hit.sku_id)
                                  );
                                } else {
                                  setSelectedSkus([]);
                                }
                              }}
                            />
                          </CanView>
                        )}
                        {header}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                {/* if custom created contains an id that is not in localHits, show those */}
                {(localHits || []).map((hit, idx) => {
                  if (customUpdated && customUpdated[hit.sku_id]) {
                    hit = customUpdated[hit.sku_id];
                    hit.sku_id = hit.id;
                  }
                  const fetchedPrice = fetchedPricing[hit.sku_id];
                  const fetchedCategory = fetchedCategories[hit.sku_id];
                  const posIntegratedKey = `${selectedLocation.store_id}_pos_integrated`;
                  const isPosIntegrated = Boolean(hit[posIntegratedKey]);
                  return (
                    <tr key={"localHits_" + hit.id}>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-96">
                        <div className="flex gap-2 items-center">
                          <CanView permissions="manager">
                            <input
                              disabled={
                                disableNav ||
                                fetchedPrice?.retailer_disabled ||
                                fetchedPrice?.disabled
                              }
                              type="checkbox"
                              className="form-checkbox"
                              checked={selectedSkus.includes(hit.sku_id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedSkus((prev) => [
                                    ...prev,
                                    hit.sku_id,
                                  ]);
                                } else {
                                  setSelectedSkus((prev) =>
                                    prev.filter((sku) => sku !== hit.sku_id)
                                  );
                                }
                              }}
                            />
                          </CanView>
                          <div className="w-10 h-10 shrink-0 mr-2 sm:mr-3 relative">
                            {/*checkbox*/}
                            <img
                              className="rounded-full"
                              src={hit.image_override || hit.image}
                              width="40"
                              height="40"
                              alt={hit.name}
                            />
                            {hit.image_override && (
                              <div
                                className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center border-2 border-white dark:border-slate-800"
                                title="Image Override Active"
                              >
                                !
                              </div>
                            )}
                          </div>
                          <div className="font-medium text-slate-800 dark:text-slate-100 text-wrap">
                            {hit.title} {hit.subtitle}{" "}
                            {hit.volume_label || hit.volume
                              ? `- ${hit.volume_label || hit.volume}`
                              : ""}
                            {hit.sku_id.startsWith("CXP-") && (
                              <span>
                                {" "}
                                <Badge color={"red"}>Custom</Badge>
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 w-36 text-wrap">
                        <div className="text-left">{hit.company}</div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 w-36 text-wrap">
                        <div className="flex items-center gap-2">
                          <div className="text-left space-y-1">
                            {view !== "allProducts" &&
                              (!fetchedCategory ? (
                                <svg
                                  className="animate-spin w-4 h-4 fill-current shrink-0"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                                </svg>
                              ) : (
                                fetchedCategory.map?.((category, idx) => (
                                  <Badge
                                    truncate
                                    color={randColor[category]}
                                    key={"category_" + idx}
                                  >
                                    <span
                                      title={category}
                                      className="max-w-[80px] truncate"
                                      key={"category_" + idx}
                                    >
                                      {category}
                                    </span>
                                  </Badge>
                                ))
                              ))}
                            {view === "allProducts" &&
                              hit.default_category.map?.((category, idx) => (
                                <Badge
                                  truncate
                                  color={randColor[category]}
                                  key={"category_" + idx}
                                >
                                  <span
                                    title={category}
                                    className="max-w-[80px] truncate"
                                    key={"category_" + idx}
                                  >
                                    {category}
                                  </span>
                                </Badge>
                              ))}
                          </div>
                          <CanView permissions="manager">
                            {view !== "allProducts" && editing?.idx === idx && (
                              <SecondaryButton
                                naked
                                onClick={() => {
                                  setNextOp("bulk-assign");
                                  setAssigningCategory([hit.sku_id]);
                                }}
                              >
                                <svg
                                  className="w-4 h-4 fill-current text-slate-500 dark:text-slate-400 shrink-0"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M11.7.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM4.6 14H2v-2.6l6-6L10.6 8l-6 6zM12 6.6L9.4 4 11 2.4 13.6 5 12 6.6z" />
                                </svg>
                              </SecondaryButton>
                            )}
                          </CanView>
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 w-24">
                        <div className="text-left">
                          <div>
                            {editing?.idx !== idx ? (
                              !fetchedPrice ? (
                                <svg
                                  className="animate-spin w-4 h-4 fill-current shrink-0"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                                </svg>
                              ) : fetchedPrice?.not_found ? (
                                "-"
                              ) : (
                                formatValue2(fetchedPrice.cost || 0)
                              )
                            ) : (
                              <input
                                className="form-input w-20"
                                type="number"
                                value={fetchedPrice?.cost || 0}
                                disabled={isPosIntegrated}
                                onChange={(e) =>
                                  setFetchedPricing((prev) => {
                                    if (!prev[hit.sku_id]) {
                                      prev[hit.sku_id] = { quantity: 0 };
                                    }
                                    if (prev[hit.sku_id].not_found) {
                                      delete prev[hit.sku_id].not_found;
                                    }
                                    prev[hit.sku_id].cost = e.target.value;

                                    return { ...prev };
                                  })
                                }
                              />
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 w-24">
                        <div className="text-left">
                          <div>
                            {editing?.idx !== idx ? (
                              !fetchedPrice ? (
                                <svg
                                  className="animate-spin w-4 h-4 fill-current shrink-0"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                                </svg>
                              ) : fetchedPrice.not_found ? (
                                "-"
                              ) : (
                                <div className="flex items-center gap-1">
                                  {fetchedPrice.discounted_price ? (
                                    <>
                                      <span className="text-green-600 font-bold">
                                        {formatValue2(
                                          fetchedPrice.discounted_price
                                        )}
                                      </span>{" "}
                                      <span className="line-through">
                                        {formatValue2(fetchedPrice.sell_price)}
                                      </span>
                                    </>
                                  ) : (
                                    formatValue2(fetchedPrice.sell_price)
                                  )}
                                  {isPosIntegrated && (
                                    <FaPlug
                                      className="w-3 h-3 text-slate-400"
                                      title="Price controlled by POS"
                                    />
                                  )}
                                </div>
                              )
                            ) : (
                              <input
                                className="form-input w-20"
                                type="number"
                                value={fetchedPrice?.sell_price || 0}
                                disabled={isPosIntegrated}
                                onChange={(e) =>
                                  setFetchedPricing((prev) => {
                                    if (!prev[hit.sku_id]) {
                                      prev[hit.sku_id] = { quantity: 0 };
                                    }
                                    if (prev[hit.sku_id].not_found) {
                                      delete prev[hit.sku_id].not_found;
                                    }
                                    prev[hit.sku_id].sell_price =
                                      e.target.value;
                                    return { ...prev };
                                  })
                                }
                              />
                            )}
                          </div>
                        </div>
                      </td>
                      {editing && (
                        <td className="px-2 first:pl-5 last:pr-5 py-3 w-24">
                          <div className="text-left">
                            <div>
                              {editing?.idx !== idx ? null : (
                                <PrimaryButton
                                  disabled={isPosIntegrated}
                                  onClick={() => {
                                    setEditPricing({
                                      pricing: fetchedPrice,
                                      sku_id: hit.sku_id,
                                      hit: hit,
                                    });
                                  }}
                                >
                                  <div className="flex gap-2 items-center">
                                    <FaPencil />
                                    {fetchedPrice.discounted_price ? (
                                      `$${fetchedPrice.discounted_price}`
                                    ) : (
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={`icon icon-tabler icon-tabler-x stroke-current ${!loading
                                          ? "text-red-500"
                                          : "text-slate-500"
                                          }`}
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path
                                          stroke="none"
                                          d="M0 0h24v24H0z"
                                          fill="none"
                                        />
                                        <path d="M18 6l-12 12" />
                                        <path d="M6 6l12 12" />
                                      </svg>
                                    )}
                                  </div>
                                </PrimaryButton>
                              )}
                            </div>
                          </div>
                        </td>
                      )}

                      <td className="px-2 first:pl-5 last:pr-5 py-3 w-18">
                        {editing?.idx !== idx ? (
                          !fetchedPricing ? (
                            <svg
                              className="animate-spin w-4 h-4 fill-current shrink-0"
                              viewBox="0 0 16 16"
                            >
                              <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                            </svg>
                          ) : fetchedPrice?.not_found ||
                            fetchedPrice?.quantity === 0 ? (
                            <span className="text-slate-500">Unpublished</span>
                          ) : fetchedPrice?.retailer_disabled ||
                            fetchedPrice?.disabled ? (
                            <span className="text-red-500">Disabled</span>
                          ) : fetchedPrice?.retailer_inactive ? (
                            <span className="text-red-500">Inactive</span>
                          ) : restrictionBySku[hit.sku_id] ? (
                            <span className="text-amber-600" title={restrictionBySku[hit.sku_id]}>
                              Restricted
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-green-500">
                              Published
                              {isPosIntegrated && (
                                <FaPlug
                                  className="w-3 h-3 text-slate-400"
                                  title="Published status controlled by POS"
                                />
                              )}
                            </span>
                          )
                        ) : (
                          <div ref={toggleSwitchRef}>
                            <ToggleSwitch
                              disabled={isPosIntegrated}
                              onChange={(val) => {
                                if (editing?.idx !== idx) {
                                  return;
                                }
                                setFetchedPricing((prev) => {
                                  if (!prev[hit.sku_id]) {
                                    prev[hit.sku_id] = {};
                                  }
                                  if (prev[hit.sku_id].not_found) {
                                    delete prev[hit.sku_id].not_found;
                                  }
                                  prev[hit.sku_id].quantity = val ? 1 : 0;
                                  return { ...prev };
                                });
                              }}
                              checked={fetchedPrice?.quantity > 0}
                            />
                          </div>
                        )}
                      </td>

                      <CanView permissions={"manager"}>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                          <div className="text-left font-medium text-sky-500">
                            {editing === null &&
                              !(
                                fetchedPrice?.retailer_disabled ||
                                fetchedPrice?.disabled
                              ) && (
                                <div className="relative inline-flex">
                                  <DropdownEditMenu align="right" disabled={disableNav} naked>
                                    <li>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const documentCount = fetchedDocumentCounts[hit.sku_id]?.count || 0;
                                          setDocumentModalOpen({
                                            sku_id: hit.sku_id,
                                            product: hit,
                                            documentCount: documentCount
                                          });
                                        }}
                                        className="font-medium text-sm flex items-center justify-between py-1 px-3 w-full text-left text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900"
                                        disabled={disableNav}
                                      >
                                        <span>Manage Documents</span>
                                        {(fetchedDocumentCounts[hit.sku_id]?.count || 0) > 0 && (
                                          <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full min-w-[1.25rem] h-5">
                                            {fetchedDocumentCounts[hit.sku_id]?.count || 0}
                                          </span>
                                        )}
                                      </button>
                                    </li>
                                    {isPosIntegrated && (
                                      <li>
                                        <button
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            setSyncingSkuId(hit.sku_id);
                                            try {
                                              const response = await createSyncNowJob({
                                                config: { sku_ids: [hit.sku_id] },
                                              });
                                              if (response.success) {
                                                dispatch(
                                                  addToast({
                                                    type: "success",
                                                    message:
                                                      "Manual sync job created for this product",
                                                    timer: 5,
                                                  })
                                                );
                                              }
                                            } catch (error) {
                                              console.error(
                                                "Failed to create manual product sync job:",
                                                error
                                              );
                                              dispatch(
                                                addToast({
                                                  type: "error",
                                                  message:
                                                    error.response?.data?.error ||
                                                    "Failed to start manual sync for this product",
                                                  timer: 5,
                                                })
                                              );
                                            } finally {
                                              setSyncingSkuId(null);
                                            }
                                          }}
                                          className="font-medium text-sm flex py-1 px-3 w-full text-left text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                          disabled={disableNav || syncingSkuId === hit.sku_id}
                                        >
                                          Manually Sync This Product
                                        </button>
                                      </li>
                                    )}
                                    <li>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setPrevSettings(
                                            JSON.parse(JSON.stringify(fetchedPrice))
                                          );
                                          const y =
                                            e.clientY + mainContainer.scrollTop; // Adjust for scroll position
                                          setEditing({ idx, y });
                                        }}
                                        className="font-medium text-sm flex py-1 px-3 w-full text-left text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900"
                                        disabled={disableNav}
                                      >
                                        Edit
                                      </button>
                                    </li>
                                    {!hit.sku_id.startsWith("CXP-") && (
                                      <>
                                        <li>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setImageModalOpen({
                                                sku_id: hit.sku_id,
                                                product: hit
                                              });
                                            }}
                                            className="font-medium text-sm flex items-center justify-between py-1 px-3 w-full text-left text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900"
                                            disabled={disableNav}
                                          >
                                            Manage Image Override
                                          </button>
                                        </li>
                                        <li>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigate(`/products/new?view=${view}`, {
                                                state: {
                                                  fromVerifiedProduct: {
                                                    title: hit.title,
                                                    subtitle: hit.subtitle,
                                                    company: hit.company,
                                                    description: hit.description || "",
                                                    volume_label: hit.volume_label || hit.volume || "",
                                                    upc_code: hit.upc_code || "",
                                                    image: hit.image,
                                                    default_category: hit.default_category?.[0] || hit.default_category,
                                                  },
                                                },
                                              });
                                            }}
                                            className="font-medium text-sm flex py-1 px-3 w-full text-left text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900"
                                            disabled={disableNav}
                                          >
                                            Create Custom Product
                                          </button>
                                        </li>
                                      </>
                                    )}
                                  </DropdownEditMenu>
                                  {(fetchedDocumentCounts[hit.sku_id]?.count || 0) > 0 && (
                                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full min-w-[1.25rem] h-5 z-10 pointer-events-none">
                                      {fetchedDocumentCounts[hit.sku_id]?.count || 0}
                                    </span>
                                  )}
                                </div>
                              )}
                            {editing?.idx === idx && (
                              <>
                                {hit.sku_id.startsWith("CXP-") && (
                                  <button
                                    onClick={() => {
                                      navigate(
                                        `/products/${hit.sku_id}?view=${view}`
                                      );
                                    }}
                                    className="btn dark:bg-theme-surface-accent border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 mr-1"
                                  >
                                    <FaFilePen />
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    setEditing(null);
                                    setFetchedPricing((prev) => {
                                      prev[hit.sku_id] = prevSettings;
                                      return { ...prev };
                                    });
                                  }}
                                  disabled={loading}
                                  className="btn dark:bg-theme-surface-accent border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 mr-1"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`icon icon-tabler icon-tabler-x stroke-current ${!loading
                                      ? "text-red-500"
                                      : "text-slate-500"
                                      }`}
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path
                                      stroke="none"
                                      d="M0 0h24v24H0z"
                                      fill="none"
                                    />
                                    <path d="M18 6l-12 12" />
                                    <path d="M6 6l12 12" />
                                  </svg>
                                </button>
                                <button
                                  onClick={async () => {
                                    if (fetchedPrice?.quantity === 0) {
                                      submit();
                                    } else {
                                      if (
                                        prevSettings.quantity === 0 ||
                                        prevSettings.not_found
                                      ) {
                                        // This was commented out becuase they don't like being asked to pick categories new products are assign to
                                        // setAssigningCategory([hit.sku_id]);
                                        // setNextOp("submit");

                                        // Doing the below three lines make menu service assign default category.
                                        setAssigningCategory([]);
                                        setAssignedCategories([]);
                                        submit();
                                      } else {
                                        const categories =
                                          await getProductCategoryAssignment(
                                            hit.sku_id
                                          );
                                        setAssignedCategories(
                                          categories.categories.reduce(
                                            (acc, cat) => {
                                              acc[cat.name] = cat;
                                              return acc;
                                            },
                                            {}
                                          )
                                        );
                                        submit();
                                      }
                                    }
                                  }}
                                  disabled={loading}
                                  className="btn dark:bg-theme-surface-accent border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                                >
                                  {loading ? (
                                    <svg
                                      className="animate-spin w-4 h-4 fill-current shrink-0"
                                      viewBox="0 0 16 16"
                                    >
                                      <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                                    </svg>
                                  ) : (
                                    <svg
                                      className="w-4 h-4 fill-current text-indigo-500 shrink-0"
                                      viewBox="0 0 16 16"
                                    >
                                      <path d="M14.3 2.3L5 11.6 1.7 8.3c-.4-.4-1-.4-1.4 0-.4.4-.4 1 0 1.4l4 4c.2.2.4.3.7.3.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4-.4-.4-1-.4-1.4 0z" />
                                    </svg>
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </CanView>
                    </tr>
                  );
                })}
              </tbody>
            </>
          )}

          {/* Table header */}
        </table>
        <div className={"mb-4 " + (editing ? "hidden" : "")}>
          <CustomPagination currentView={view} />
        </div>
        <PricingModal
          pricing={editPricing?.pricing}
          sku_id={editPricing?.sku_id}
          hit={editPricing?.hit}
          setFetchedPricing={setFetchedPricing}
          setEditPricing={setEditPricing}
        />
        <BulkPricingModal
          nextOp={nextOp}
          sku_ids={bulkEditingPrice}
          setSkuIds={setBulkEditingPrice}
          setNextOp={setNextOp}
          setFetchedPricing={setFetchedPricing}
          setForceUpdateCategories={setForceUpdateCategories}
        />
      </>
    );
  }
);

const BulkPricingModal = ({
  nextOp,
  sku_ids,
  setSkuIds,
  setNextOp,
  setFetchedPricing,
  setForceUpdateCategories,
}) => {
  const [cost, setCost] = useState(0);
  const [sellPrice, setSellPrice] = useState(0);
  const [discountedPrice, setDiscountedPrice] = useState(0);
  const [discount, setDiscount] = useState(false);
  const discountInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { selectedMerchant, selectedLocation } = useSelector(
    (state) => state.app
  );
  const [publishToLocation, setPublishToLocation] = useState([]);

  useEffect(() => {
    setCost(0);
    setSellPrice(0);
    setDiscountedPrice(0);
    setDiscount(false);
  }, [nextOp]);

  useEffect(() => {
    if (discount) {
      discountInputRef.current.select();
    }
  }, [discount]);
  useEffect(() => {
    setPublishToLocation([selectedLocation.store_id]);
  }, [selectedLocation]);

  const effectivePublishPrice = discount ? discountedPrice : sellPrice;
  const bulkPublishPriceInvalid =
    nextOp === "bulk-publish" &&
    (!Number.isFinite(effectivePublishPrice) ||
      effectivePublishPrice <= 0);

  let width = selectedMerchant?.locations?.length > 1 ? "3xl" : "lg";
  if (nextOp === "bulk-unpublish") {
    width = "sm";
  }
  return (
    <ModalBasic
      modalOpen={sku_ids.length > 0}
      setModalOpen={() => setSkuIds([])}
      width={width}
      title={
        <div>
          {(() => {
            const actions = {
              "bulk-publish": "Publish",
              "bulk-unpublish": "Unpublish",
              "bulk-deactivate": "Deactivate",
              "bulk-reactivate": "Reactivate",
              "bulk-edit-price": "Edit Price for",
            };
            return `${actions[nextOp]} ${sku_ids.length} product${sku_ids.length > 1 ? "s" : ""
              }`;
          })()}
        </div>
      }
    >
      <div>
        <div className="flex gap-8">
          {selectedMerchant?.locations?.length > 1 && (
            <div className="text-sm">
              <div>
                {nextOp === "bulk-publish" && "Publish to these locations:"}
                {nextOp === "bulk-unpublish" &&
                  "Unpublish from these locations:"}
                {nextOp === "bulk-deactivate" &&
                  `Confirm you wish to deactivate ${sku_ids.length} product${sku_ids.length > 1 ? "s" : ""
                  } in all locations`}
                {nextOp === "bulk-reactivate" &&
                  `Confirm you wish to reactivate ${sku_ids.length} product${sku_ids.length > 1 ? "s" : ""
                  } in all locations`}
              </div>
              {!["bulk-deactivate", "bulk-reactivate"].includes(nextOp) && (
                <div>
                  <div className="flex gap-2 items-center">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      checked={
                        publishToLocation.length ===
                        selectedMerchant.locations.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setPublishToLocation(
                            selectedMerchant.locations.map(
                              (loc) => loc.store_id
                            )
                          );
                        } else {
                          setPublishToLocation([]);
                        }
                      }}
                    />
                    <span>All Locations</span>
                  </div>
                  {selectedMerchant.locations.map((location) => (
                    <div
                      key={location.store_id}
                      className="flex gap-2 items-center"
                    >
                      <input
                        type="checkbox"
                        className="form-checkbox"
                        checked={publishToLocation.includes(location.store_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setPublishToLocation((prev) => [
                              ...prev,
                              location.store_id,
                            ]);
                          } else {
                            setPublishToLocation((prev) =>
                              prev.filter((loc) => loc !== location.store_id)
                            );
                          }
                        }}
                      />
                      <span>{location.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {(nextOp === "bulk-publish" || nextOp === "bulk-edit-price") && (
            <div className="space-y-4 flex flex-col flex-1">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="email"
                >
                  Cost
                </label>
                <div className="flex gap-1 items-center">
                  <span>$</span>
                  <input
                    className="form-input w-full"
                    type="number"
                    value={cost}
                    onChange={(e) => {
                      setCost(parseFloat(e.target.value));
                    }}
                  />
                </div>
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="email"
                >
                  Regular Price
                </label>
                <div className="flex gap-1 items-center">
                  <span>$</span>
                  <input
                    className="form-input w-full"
                    type="number"
                    value={sellPrice}
                    onChange={(e) => {
                      setSellPrice(parseFloat(e.target.value));
                    }}
                  />
                </div>
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1 flex gap-2"
                  htmlFor="email"
                >
                  Sales Price{" "}
                  <ToggleSwitch
                    checked={discount}
                    onChange={(val) => {
                      setDiscount(val);
                    }}
                    onLabel={"On"}
                    offLabel={"Off"}
                  />
                </label>

                <div
                  className={`flex gap-1 items-center ${discount ? "" : "hidden"
                    }`}
                >
                  <span>$</span>
                  <input
                    ref={discountInputRef}
                    className="form-input w-full"
                    type="number"
                    value={discountedPrice}
                    onChange={(e) => {
                      setDiscountedPrice(parseFloat(e.target.value));
                    }}
                  />
                </div>
              </div>
              {nextOp === "bulk-publish" && bulkPublishPriceInvalid && (
                <p className="text-sm text-rose-600">
                  Set a regular price or sale price above $0 to publish.
                </p>
              )}
            </div>
          )}
        </div>
        <div>
          <div>
            <div className="flex gap-2 w-full justify-end">
              <SecondaryButton
                loading={loading}
                onClick={() => {
                  setNextOp(null);
                  setSkuIds([]);
                }}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton
                loading={loading}
                disabled={
                  publishToLocation.length === 0 || bulkPublishPriceInvalid
                }
                onClick={async () => {
                  try {
                    if (bulkPublishPriceInvalid) {
                      dispatch(
                        addToast({
                          type: "error",
                          message:
                            "Cannot publish with a $0 price. Enter an amount greater than $0.",
                          timer: 5,
                        })
                      );
                      return;
                    }
                    if (
                      ["bulk-deactivate", "bulk-reactivate"].includes(nextOp)
                    ) {
                      const payload = {
                        sku_ids: sku_ids,
                        inactive: nextOp === "bulk-deactivate",
                      };
                      setLoading(true);
                      await bulkSetProductInactive(payload);
                      setLoading(false);
                      setSkuIds([]);
                      setForceUpdateCategories(true);
                      setFetchedPricing((prev) => {
                        sku_ids.forEach((sku_id) => {
                          prev[sku_id] = {
                            ...prev[sku_id],
                            retailer_inactive: nextOp === "bulk-deactivate",
                          };
                        });
                        return { ...prev };
                      });
                      return;
                    }
                    const payload = {
                      sku_ids: sku_ids,
                      categories: [],
                    };
                    if (
                      nextOp === "bulk-publish" ||
                      nextOp === "bulk-edit-price"
                    ) {
                      payload.price = sellPrice;
                      payload.cost = cost;
                      payload.discounted_price = discount ? discountedPrice : 0;
                      payload.quantity =
                        nextOp === "bulk-publish" ? 1 : "UNCHANGED";
                    } else if (nextOp === "bulk-unpublish") {
                      payload.quantity = 0;
                    }
                    if (publishToLocation.length > 0) {
                      payload.store_ids = publishToLocation;
                    }
                    setLoading(true);
                    await editProducts(payload);
                    setLoading(false);
                    setForceUpdateCategories(sku_ids);

                    setSkuIds([]);

                    setFetchedPricing((prev) => {
                      sku_ids.forEach((sku_id) => {
                        prev[sku_id] = {
                          cost,
                          sell_price:
                            nextOp === "bulk-unpublish"
                              ? prev[sku_id]?.sell_price
                              : sellPrice,
                          discounted_price:
                            nextOp === "bulk-unpublish"
                              ? prev[sku_id]?.discounted_price
                              : discount
                                ? discountedPrice
                                : 0,
                          quantity:
                            nextOp === "bulk-publish"
                              ? 1
                              : nextOp === "bulk-unpublish"
                                ? 0
                                : prev[sku_id]?.quantity,
                        };
                      });
                      return { ...prev };
                    });
                    setNextOp(null);
                    dispatch(
                      addToast({
                        type: "success",
                        message: `Successfully ${nextOp === "bulk-publish"
                          ? "published"
                          : "unpublished"
                          } ${sku_ids.length} products`,
                        timer: 5,
                      })
                    );
                  } catch (e) {
                    let message = "please try again later";
                    try {
                      const serverMessage = e.response?.data?.error;
                      if (serverMessage) {
                        message = serverMessage;
                      }
                    } catch { }

                    dispatch(
                      addToast({
                        type: "error",
                        message: "Error updating pricing, " + message,
                        timer: 5,
                      })
                    );
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                OK
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </ModalBasic>
  );
};

const PricingModal = ({
  pricing,
  sku_id,
  setEditPricing,
  setFetchedPricing,
  hit,
}) => {
  const [_pricing, setPricing] = useState(null);
  const [_sku_id, setSkuId] = useState(sku_id);
  const [discount, setDiscount] = useState(Boolean(pricing?.discounted_price));
  const isPosIntegrated = Boolean(
    hit?.[`${hit?.store_id || ""}_pos_integrated`]
  );

  useEffect(() => {
    if (pricing) {
      setPricing(JSON.parse(JSON.stringify(pricing)));
    }
    setSkuId(sku_id);
    setDiscount(Boolean(pricing?.discounted_price));
  }, [pricing, sku_id]);

  useEffect(() => {
    if (!_sku_id) {
      setEditPricing(null);
    }
  }, [_sku_id]);
  const [discountInputRef, setDiscountInputRef] = useState(null);

  useEffect(() => {
    if (discountInputRef) {
      discountInputRef.select();
    }
  }, [discountInputRef]);


  const discountInputEle = useCallback(node => {
    setDiscountInputRef(node);
  }, []);

  return (
    <ModalBasic
      modalOpen={Boolean(_sku_id)}
      setModalOpen={setSkuId}
      width="xl"
      title={
        <div>
          {hit?.title} {hit?.subtitle} ({hit?.volume_label})
        </div>
      }
    >
      <div className="space-y-4 flex">
        <div className="flex flex-1 flex-col items-center">
          <img
            src={hit?.image}
            alt={hit?.title}
            className="w-3/4 h-3/4 object-cover"
          />
        </div>
        <div className="space-y-4 flex flex-col flex-1">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="email">
              Cost
            </label>
            <div className="flex gap-1 items-center">
              <span>$</span>
              <input
                className="form-input w-full"
                type="number"
                value={_pricing?.cost || 0}
                disabled={isPosIntegrated}
                onChange={(e) => {
                  setPricing((prev) => {
                    prev.cost = e.target.value;
                    return { ...prev };
                  });
                }}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="email">
              Regular Price
            </label>
            <div className="flex gap-1 items-center">
              <span>$</span>
              <input
                className="form-input w-full"
                type="number"
                value={_pricing?.sell_price || 0}
                disabled={isPosIntegrated}
                onChange={(e) => {
                  setPricing((prev) => {
                    prev.sell_price = e.target.value;
                    return { ...prev };
                  });
                }}
              />
            </div>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-1 flex gap-2"
              htmlFor="email"
            >
              Sales Price{" "}
              <ToggleSwitch
                checked={discount}
                disabled={isPosIntegrated}
                onChange={(val) => {
                  setDiscount(val);
                }}
                onLabel={"On"}
                offLabel={"Off"}
              />
            </label>

            <div
              className={`flex gap-1 items-center ${discount ? "" : "hidden"}`}
            >
              <span>$</span>
              <input
                ref={discountInputEle}
                className="form-input w-full"
                type="number"
                value={_pricing?.discounted_price || 0}
                disabled={isPosIntegrated}
                onChange={(e) => {
                  setPricing((prev) => {
                    prev.discounted_price = e.target.value;
                    return { ...prev };
                  });
                }}
              />
            </div>
          </div>

          <div className="flex gap-2 w-full justify-end">
            <SecondaryButton
              onClick={() => {
                setSkuId(null);
              }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={() => {
                setFetchedPricing((prev) => {
                  prev[sku_id].cost = _pricing.cost;
                  prev[sku_id].sell_price = _pricing.sell_price;
                  if (discount) {
                    prev[sku_id].discounted_price = _pricing.discounted_price;
                  } else {
                    prev[sku_id].discounted_price = 0;
                  }

                  return { ...prev };
                });
                setSkuId(null);
              }}
            >
              OK
            </PrimaryButton>
          </div>
        </div>
      </div>
    </ModalBasic>
  );
};
export default ProductsHits;
